<template>
  <div class="d-flex flex-column">
    <!-- Drill-down Learn More View -->
    <div v-if="showLearnMoreView && selectedCheckpoint" class="px-5">
      <AsEmployeeCheckpointLearnMoreCard
        :prefix="selectedCheckpoint.prefix"
        :video_url="selectedCheckpoint.video_url"
        :status="selectedCheckpoint.status"
        :responsibility="selectedCheckpoint.responsibility"
        :role="selectedCheckpoint.role"
        :position="selectedCheckpoint.position"
        :readiness_level="selectedCheckpoint.readiness_level"
        :criteria="selectedCheckpoint.criteria"
        :instructions="selectedCheckpoint.instructions"
        :inspector_info="selectedCheckpoint.inspector_info"
        :set_to_microlearning_instructions="selectedCheckpoint.set_to_microlearning_instructions"
        :microlearning_instructions="selectedCheckpoint.microlearning_instructions"
        @back-to-checklist="handleBackFromLearnMore"
      />
    </div>

    <!-- Main Checklist View -->
    <div v-else-if="shouldShowSearchAndTable" class="">
      <div class="px-5 mb-5">
        <SearchBar
          @onSearch="onFilter"
          type="button"
          :label="appliedFilters.length > 0 ? 'More Filters' : 'Filters'"
          :length="appliedFilters.length"
          :chipText="appliedFilters"
          ghostText="Search"
          :add="false"
          @onClick="onAddFilter($event)"
          :valueSearch="filterText"
          class="checkpoint-add-btn"
          @removeFilter="removeFilter"
          :counterSection="true"
          :levelCounter="props.levelCounter"
          @filterTypes="updateSelectedLevels"
          :removeCounterLevelFilter="removeCounterLevelFilter"
        />
      </div>

      <div class="text-caption-2-medium mb-3 checkpoint-inspected">
        Checkpoints to be inspected
      </div>

      <div v-if="loading" class="px-5">
        <SharedTableSkeleton
          :row-count="paginationModel.perPage"
          :column-count="3"
          column-template="3fr 1fr 120px"
        />
      </div>

      <div v-else-if="paginatedCheckpoints.length > 0">
        <div
          class="px-5 mb-3"
          v-for="(item, index) in paginatedCheckpoints"
          :key="index"
        >
          <AsEmployeeCheckpointCard
            :ideaMDP452="ideaMDP452"
            :level="item.level"
            :userInfo="item.userInfo"
            :criteria="item.criteria"
            :criteria_with_prefix="item.criteria_with_prefix || ''"
            :instructions="item.instructions"
            :description="item.description || null"
            :video="item.video"
            :inspection_status="item.inspection_status"
            :reason="item.reason"
            label="Learn More"
            class="pre-btn-ins"
            :checkpointInfo="item"
            :showChatter="item.showChatter === true"
            :type="checkpointRowType(item)"
            :quiz="checkpointRowQuiz(item)"
            :inspection="checkpointRowInspection(item)"
            :video_thumbnail="item.video_thumbnail || item.thumbnail_url || ''"
            :chatter-icon-src="item.chatterIconSrc || null"
            @learn-more-click="handleLearnMoreClick"
            @mdp-cta="emit('mdp-cta', $event)"
            @complete-microlearning-click="emit('complete-microlearning-click', $event)"
            @request-more-checkpoints-chatter="emit('request-more-checkpoints-chatter', $event)"
            :videoThumbnail="item.set_to_microlearning_instructions ? item.video : ''"
            :prefix="item.prefix"
          />
        </div>

        <ExPagination
          v-if="paginationModel.total > paginationModel.perPage"
          :pagination="paginationModel"
          @onRequest="requestData"
        />
      </div>

      <NoRecordFound
        v-else
        title=""
        message="checkpoints"
        btnTitle=""
        :shouldShowAddAction="false"
        :showActiveCounter="false"
        :noBorder="true"
        :showClearAction="true"
        :clearLabel="clearButtonLabel"
        showSpecificMessage="No checkpoints found"
        description="No checkpoints match your current search or filter criteria. Try adjusting your filters or search term."
        @onClear="clearFilters"
      />
    </div>

    <div v-else-if="loading" class="px-5">
      <SharedTableSkeleton
        :row-count="paginationModel.perPage"
        :column-count="3"
        column-template="3fr 1fr 120px"
      />
    </div>

    <NoRecordFound
      v-else
      title=""
      message="checkpoints"
      btnTitle=""
      :shouldShowAddAction="false"
      :showActiveCounter="false"
      :noBorder="true"
      showSpecificMessage="This Responsibility has no Inspection Checklist"
      description="This means the inspector will describe verbally what they expect from you at inspection time and they will <b>Give you a Badge</b> as opposed to Award a badge.<br><br>When a Responsibility has a Checklist, then the inspector can pass/fail checkpoints during inspection by following the checklist and can <b>Award a badge</b>."
    />

    <SearchModal
      :dialog="showFilterDialog"
      @back="showFilterDialog = false"
      @checkedItemsChanged="filteredModal"
      :clearSelection="clearSelection"
      filterType="Checkpoint"
      :groupsItems="groupsItems"
      :modalWidth="620"
      :statusOptions="[]"
      :filterGroups="[]"
      :defaultSelected="appliedFilters"
      class="filter-accounts"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import AsEmployeeCheckpointCard from "@components/AsEmployeeCheckpointCard/AsEmployeeCheckpointCard.vue";
import AsEmployeeCheckpointLearnMoreCard from "@components/AsEmployeeCheckpointLearnMoreCard/AsEmployeeCheckpointLearnMoreCard.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import SharedTableSkeleton from "@components/SharedTableSkeleton/SharedTableSkeleton.vue";

const MDP_TYPES = ["MQI", "MI", "MQ", "NONE"];
const MDP_QUIZ = ["Pending", "Passed", "Passed and Done", "Failed", "None"];
const MDP_INSPECTION = ["Pending", "Passed", "Failed", "Not Applicable", "None"];

function checkpointRowType(item) {
  const t = item.microlearning_type ?? item.microlearningType;
  if (MDP_TYPES.includes(t)) return t;
  if (MDP_TYPES.includes(item.type)) return item.type;
  return "NONE";
}

function checkpointRowQuiz(item) {
  const q = item.quiz ?? item.quiz_completion_status;
  if (MDP_QUIZ.includes(q)) return q;
  if (checkpointRowType(item) === "NONE") return "None";
  return "Pending";
}

function checkpointRowInspection(item) {
  if (MDP_INSPECTION.includes(item.inspection)) return item.inspection;
  const raw = (item.inspection_status || "").toString();
  const s = raw.toLowerCase();
  if (s === "passed") return "Passed";
  if (s === "failed") return "Failed";
  if (s === "n/a" || s === "na" || s === "not applicable") return "Not Applicable";
  if (s === "none") return "None";
  return "Pending";
}

// ✅ Props
const props = defineProps({
  /** idea-MDP-452: use `AsEmployeeCheckpointCard` compact criteria + knowledge row layout. */
  ideaMDP452: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Array,
    default: () => [],
  },
  pagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10,
    }),
  },
  levelCounter: {
    type: Object,
    default: () => ({
      Apprentice: 0,
      Professional: 0,
      Coach: 0,
      Master: 0,
    }),
  },
});

// ✅ Emits
const emit = defineEmits([
  "appliedFilter",
  "filtersChanged",
  "paginationChanged",
  "mdp-cta",
  "complete-microlearning-click",
  "request-more-checkpoints-chatter",
]);

// ✅ State (refs)
const filterText = ref("");
const showFilterDialog = ref(false);
const showLearnMoreView = ref(false);
const selectedCheckpoint = ref(null);
const appliedFilters = ref(["Not Yet Passed"]);
const defaultSelected = ref(["Not Yet Passed"]);
const modalDialog = ref(false);
const paginationModel = ref({ ...props.pagination });
const checkpoints = ref([...props.data]);
const searchDebounceTimer = ref(null);

const groupsItems = ref([
  {
    groups: [
      {
        type: "radio",
        label: "Status",
        items: [
          { label: "No Filter", value: "no_filter" },
          { label: "Not Yet Passed", value: "not_yet_passed" },
          { label: "Only Failed", value: "failed" },
          { label: "Only Not Yet Evaluated", value: "not_yet_evaluated" },
          { label: "Only Reset", value: "reset" },
          { label: "Only Not Applicable", value: "not_applicable" },
          { label: "Only Passed", value: "passed" },
        ],
      },
    ],
  },
]);
const clearSelection = ref("");
const removeCounterLevelFilter = ref(false);
const selectedLevels = ref([]);
const levelOrder = ["Apprentice", "Professional", "Coach", "Master"];

// ✅ Computed
// Server-side filtering: display all results from API (no client-side filtering)
// Search and level filters are handled server-side via API calls
const filteredCheckpoints = computed(() => {
  // Just sort the results (sorting can be client-side if not handled by server)
  let resultArray = [...checkpoints.value];
  
  // Sort by level and then by sequence
  resultArray.sort((a, b) => {
    const levelA = levelOrder.indexOf(a.level);
    const levelB = levelOrder.indexOf(b.level);

    if (levelA === levelB) {
      // If levels are the same, sort by sequence (assuming 'sequence' exists)
      return (a.sequence || 0) - (b.sequence || 0);
    }
    return levelA - levelB;
  });

  return resultArray;
});

const clearButtonLabel = computed(() => {
  if (appliedFilters.value.length > 0 && filterText.value.length > 0) {
    return "Clear All";
  } else if (appliedFilters.value.length > 0) {
    return "Clear Filters";
  } else if (filterText.value.length > 0) {
    return "Clear Search";
  } else {
    return "Clear All";
  }
});

// Server-side pagination: display all results from API (no client-side pagination)
const paginatedCheckpoints = computed(() => {
  return filteredCheckpoints.value;
});

// ✅ Counters
// Level counters come from summary-by-level API response (props.levelCounter)
// These represent the total counts across all pages, not just the current page

// ✅ Computed - Show search bar and table if filters/search applied OR if there are checkpoints
const shouldShowSearchAndTable = computed(() => {
  const hasSearch = filterText.value && filterText.value.length > 0;
  const hasFilters = appliedFilters.value && appliedFilters.value.length > 0;
  const hasLevelFilters = selectedLevels.value && selectedLevels.value.length > 0;
  const hasCheckpoints = paginationModel.value.total > 0;
  
  // Show if any search/filter is applied OR if there are checkpoints
  return (hasSearch || hasFilters || hasLevelFilters) || hasCheckpoints;
});

// ✅ Lifecycle
onMounted(() => {
  // Total comes from server-side pagination, not client-side filtering
  // paginationModel.value.total is set from props.pagination
  appliedFilters.value = ["Not Yet Passed"];
  
  // Emit filtersChanged on mount with initial filters
  const queryParams = buildQueryParams(appliedFilters.value);
  emit("filtersChanged", {
    filters: appliedFilters.value,
    queryParams: queryParams,
    levelFilters: selectedLevels.value,
    search: filterText.value || null,
  });
  
  emit("appliedFilter", appliedFilters.value);
});

// Cleanup debounce timer on unmount
onUnmounted(() => {
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }
});

// ✅ Watchers
watch(
  () => props.data,
  (newVal) => {
    checkpoints.value = newVal;
    // Don't reset page on data change - let server-side pagination handle it
  }
);

watch(
  () => props.pagination,
  (newVal) => {
    if (newVal) {
      // Ensure total is properly set from server response (count from API)
      paginationModel.value = { 
        page: newVal.page || 1,
        total: newVal.total || 0,
        perPage: newVal.perPage || 10
      };
    }
  },
  { deep: true, immediate: true }
);

// Total comes from server-side pagination via props, not from filtered results
// watch(filteredCheckpoints, (newVal) => {
//   paginationModel.value.total = newVal.length;
// });

// Level filter changes are now handled in updateSelectedLevels function
// which emits filtersChanged to trigger server-side API call
// watch(selectedLevels, () => {
//   paginationModel.value.page = 1;
// });

// ✅ Methods
function clearFilters() {
  appliedFilters.value = [];
  selectedLevels.value = [];
  removeCounterLevelFilter.value = false;
  filterText.value = "";
  paginationModel.value.page = 1;
  
  // Emit filtersChanged with empty query params
  emit("filtersChanged", {
    filters: [],
    queryParams: {},
    levelFilters: selectedLevels.value,
    search: null,
  });
  
  emit("appliedFilter", appliedFilters.value);
}
function updateSelectedLevels(levels) {
  if (removeCounterLevelFilter.value) {
    removeCounterLevelFilter.value = false;
  }
  selectedLevels.value = levels;
  
  // Emit filtersChanged to trigger server-side API call with level filters
  const queryParams = buildQueryParams(appliedFilters.value);
  emit("filtersChanged", {
    filters: appliedFilters.value,
    queryParams: queryParams,
    levelFilters: levels,
    search: filterText.value || null,
  });
  
  // Reset to page 1 when filters change
  paginationModel.value.page = 1;
}

function onAddFilter(type) {
  if (type === "filter") {
    showFilterDialog.value = true;
  }
}

// Helper function to map filter label to query param key
// Based on the API query params pattern: ?not_yet_passed=true, ?only_failed=true, etc.
function getFilterQueryParam(filterLabel) {
  const filterMap = {
    "No Filter": "no_filter",
    "Not Yet Passed": "not_yet_passed",
    "Only Failed": "only_failed",
    "Only Not Yet Evaluated": "only_not_yet_evaluated",
    "Only Reset": "only_reset",
    "Only Not Applicable": "only_not_applicable",
    "Only Passed": "only_passed",
  };
  return filterMap[filterLabel] || null;
}

// Helper function to build query params object
function buildQueryParams(filterLabels) {
  const queryParams = {};
  filterLabels.forEach((label) => {
    const paramKey = getFilterQueryParam(label);
    if (paramKey) {
      queryParams[paramKey] = true;
    }
  });
  return queryParams;
}

function filteredModal(filters) {
  // Extract labels from filter items
  // For radio buttons, the modal returns objects with { labelType, value } where value is the label
  const newArray = filters
    .map((item) => {
      if (typeof item === "string") {
        return item;
      } else if (typeof item === "object" && item.hasOwnProperty("value")) {
        // For radio buttons, value contains the label (e.g., "Not Yet Passed")
        return item.value;
      }
      return null;
    })
    .filter(Boolean);

  appliedFilters.value = newArray;
  
  // Build query params and emit filtersChanged event for API call
  const queryParams = buildQueryParams(newArray);
  emit("filtersChanged", {
    filters: newArray,
    queryParams: queryParams,
    levelFilters: selectedLevels.value,
    search: filterText.value || null,
  });
  
  emit("appliedFilter", appliedFilters.value);
  showFilterDialog.value = false;
}

function removeFilter(removedFilter) {
  clearSelection.value = removedFilter;
  
  // Remove the filter from appliedFilters
  appliedFilters.value = appliedFilters.value.filter(
    (filter) => filter !== removedFilter
  );
  
  // Build query params and emit filtersChanged event for API call
  const queryParams = buildQueryParams(appliedFilters.value);
  emit("filtersChanged", {
    filters: appliedFilters.value,
    queryParams: queryParams,
    levelFilters: selectedLevels.value,
    search: filterText.value || null,
  });
  
  emit("appliedFilter", appliedFilters.value);
  paginationModel.value.page = 1;
}

function onFilter(data) {
  filterText.value = data;
  
  // Clear any existing debounce timer
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }
  
  // Debounce the search API call by 500ms
  searchDebounceTimer.value = setTimeout(() => {
    // Emit filtersChanged to trigger server-side API call with search
    const queryParams = buildQueryParams(appliedFilters.value);
    emit("filtersChanged", {
      filters: appliedFilters.value,
      queryParams: queryParams,
      levelFilters: selectedLevels.value,
      search: data || null,
    });
    
    // Reset to page 1 when search changes
    if (data) {
      paginationModel.value.page = 1;
    }
  }, 500);
}

function onClick() {
  modalDialog.value = !modalDialog.value;
}

async function requestData(data) {
  if (data && data.page) {
    paginationModel.value.page = data.page;
    // Emit pagination change event to parent for server-side pagination
    emit("paginationChanged", {
      page: data.page,
      perPage: paginationModel.value.perPage,
    });
  }
}

function handleLearnMoreClick(checkpointData) {
  selectedCheckpoint.value = checkpointData;
  showLearnMoreView.value = true;
}

function handleBackFromLearnMore() {
  showLearnMoreView.value = false;
  selectedCheckpoint.value = null;
}
</script>

<style>
.checkpoint-inspected {
  background-color: #f9fafb;
  border-bottom: 1px solid #eaecf0;
  padding: 12px 35px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #667085;
}
.checkpoint-add-btn .storybook-button--btn-normal-hover-icon {
  font-size: 14px !important;
}
</style>
